import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { DotLottieReact, setWasmUrl } from "@lottiefiles/dotlottie-react";
import dotLottieWasmUrl from "@lottiefiles/dotlottie-web/dotlottie-player.wasm?url&no-inline";
import { AtSign, Check, ExternalLink, GripVertical, Hash, Image, MessageSquareText, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import checkmarkAnimation from "../../../../checkmark.lottie?url&no-inline";
import bchannelLogo from "../../../../logo.png";
import { reorderSections } from "../../../shared/composer-order";
import { appendBeforeTrailingPing, normalizeComposerPing } from "../../../shared/composer-ping";
import { extractMentionIds, labelMentions } from "../../../shared/mrkdwn";
import { POSTING_PERMISSION_ERROR, POSTING_PERMISSION_HELP_IMAGE } from "../../../shared/posting-permissions";
import type { ComposerDraft, DraftDocument, MessageSection } from "../../../shared/types";
import { ApiError, api } from "../../api";
import { AccessRecovery, BannedState, SaveIndicator, type SaveState } from "../../components/AppStates";
import { LoadingRaccoon, newId } from "../../components/primitives";
import { clearHistory, type HistorySnapshot, loadHistory, MAX_REDO, MAX_UNDO, saveHistory } from "../../undo-history";
import { ActionsEditor } from "./ActionsEditor";
import { MessagePreview } from "./MessagePreview";
import { PingPermissionCard } from "./PingPermissionCard";
import { ResultsPanel } from "./ResultsPanel";
import { TextEditor } from "./RichTextEditor";
import {
  ContextEditor,
  HeadingEditor,
  MarkdownEditor,
  MediaEditor,
  SectionAccordionHeader,
  SectionControls,
  TableEditor,
  VoteEditor,
} from "./SectionEditors";
import { SenderNamePicker } from "./SenderNamePicker";

setWasmUrl(dotLottieWasmUrl);

function sectionLabel(section?: MessageSection) {
  if (!section) return "Section";
  switch (section.type) {
    case "text":
      return "Message";
    case "heading":
      return "Heading";
    case "context":
      return "Context";
    case "table":
      return "Table";
    case "image":
      return "Image";
    case "video":
      return "Video";
    case "file":
      return "File";
    case "markdown":
      return "Markdown";
    case "actions":
      return "Actions";
    case "vote":
      return "Vote";
    case "divider":
      return "Divider";
  }
}

function SortableSection({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: (props: {
    setNodeRef: (node: HTMLElement | null) => void;
    dragHandle: ReactNode;
    stateClass: string;
  }) => ReactNode;
}) {
  const { attributes, listeners, setActivatorNodeRef, setNodeRef, isDragging, isOver } = useSortable({ id });
  const stateClass = `${isDragging ? " is-dragging" : ""}${isOver && !isDragging ? " is-drop-target" : ""}`;

  return children({
    setNodeRef,
    stateClass,
    dragHandle: (
      <button
        ref={setActivatorNodeRef}
        type="button"
        className="section-drag-handle"
        aria-label={`Drag to move ${label}`}
        title={`Drag to move ${label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>
    ),
  });
}

function ComposerApp() {
  const pathDraftId = location.pathname.match(/^\/compose\/([a-f0-9-]+)$/)?.[1];
  const [draft, setDraft] = useState<ComposerDraft | null>(null);
  const [document, setDocument] = useState<DraftDocument | null>(null);
  const [title, setTitle] = useState("");
  const [saveState, setSaveState] = useState<SaveState>("saved");
  const [error, setError] = useState("");
  const [errorDetail, setErrorDetail] = useState("");
  const [blockErrors, setBlockErrors] = useState<Array<{ sectionId: string; message: string }>>([]);
  const [recoverySignedIn, setRecoverySignedIn] = useState(false);
  const [banned, setBanned] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendCountdown, setSendCountdown] = useState<number | null>(null);
  const [justSent, setJustSent] = useState(false);
  const [editingSent, setEditingSent] = useState(false);
  const [openSectionId, setOpenSectionId] = useState<string | null>(null);
  const [pingPermission, setPingPermission] = useState<Awaited<ReturnType<typeof api.pingPermission>> | null>(null);
  const [pingModalOpen, setPingModalOpen] = useState(false);
  const [pingNeedsRequest, setPingNeedsRequest] = useState(false);
  const [checkingPing, setCheckingPing] = useState(false);
  const [identities, setIdentities] = useState<Array<{ id: string; name: string; avatarUrl: string }>>([]);
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const fileInput = useRef<HTMLInputElement>(null);
  const cancelSendRef = useRef<HTMLButtonElement>(null);
  const versionRef = useRef(1);
  const revisionRef = useRef(0);
  const savedRevisionRef = useRef(0);
  const savedDocumentRef = useRef<DraftDocument | null>(null);
  const savedTitleRef = useRef("");
  const saveStateRef = useRef<SaveState>("saved");
  const documentRef = useRef<DraftDocument | null>(null);
  const titleRef = useRef("");
  const undoRef = useRef<HistorySnapshot[]>([]);
  const redoRef = useRef<HistorySnapshot[]>([]);
  const lastUndoAtRef = useRef(0);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const draftIdRef = useRef(pathDraftId);

  useEffect(() => {
    saveStateRef.current = saveState;
  }, [saveState]);
  useEffect(() => {
    documentRef.current = document;
  }, [document]);
  useEffect(() => {
    titleRef.current = title;
  }, [title]);

  const load = useCallback(async (id: string) => {
    const value = await api.draft(id);
    const normalizedDocument = normalizeComposerPing(value.document, value.ping);
    setDraft(value);
    setDocument(normalizedDocument);
    setTitle(value.title);
    setError(value.channelAccessWarning ?? "");
    versionRef.current = value.version;
    revisionRef.current = 0;
    savedRevisionRef.current = 0;
    savedDocumentRef.current = normalizedDocument;
    savedTitleRef.current = value.title;
    documentRef.current = normalizedDocument;
    titleRef.current = value.title;
    setSaveState("saved");
    const history = loadHistory(id);
    undoRef.current = history.undo;
    redoRef.current = history.redo;
    api
      .identities()
      .then(setIdentities)
      .catch(() => undefined);
    // Detect up front whether a non-manager needs to ask permission to ping,
    // so the send button can show the request-send state before they hit send.
    if (value.ping !== "none") {
      api
        .pingPermission(id)
        .then((context) => {
          setPingPermission(context);
          setPingNeedsRequest(!context.allowed);
        })
        .catch(() => undefined);
    } else {
      setPingPermission(null);
      setPingNeedsRequest(false);
    }
    const allMrkdwn = normalizedDocument.sections
      .filter((section) => section.type === "text")
      .map((section) => section.mrkdwn)
      .join(" ");
    const { userIds, channelIds } = extractMentionIds(allMrkdwn);
    if (userIds.length || channelIds.length) {
      api
        .resolveMentions(userIds, channelIds)
        .then((resolved) => {
          const users = new Map(resolved.users.map((user) => [user.id, user.name]));
          const channels = new Map(resolved.channels.map((channel) => [channel.id, channel.name]));
          setDocument((current) =>
            current
              ? {
                  ...current,
                  sections: current.sections.map((section) =>
                    section.type === "text"
                      ? { ...section, mrkdwn: labelMentions(section.mrkdwn, users, channels) }
                      : section,
                  ),
                }
              : current,
          );
        })
        .catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    if (pathDraftId)
      load(pathDraftId).catch((cause) => {
        if (cause instanceof ApiError && cause.status === 403 && cause.message.includes("banned")) {
          setBanned(true);
          return;
        }
        setError(cause.message);
        api
          .home()
          .then(() => setRecoverySignedIn(true))
          .catch(() => setRecoverySignedIn(false));
      });
    else
      api
        .bootstrap()
        .then(({ draftId }) => {
          history.replaceState(null, "", `/compose/${draftId}`);
          draftIdRef.current = draftId;
          return load(draftId);
        })
        .catch(() => setError("Open bchannel from /bchannel, /bhere, or /bmsg in Slack."));
  }, [load, pathDraftId]);

  useEffect(() => {
    if (!previewOpen && sendCountdown === null) return;
    const closeOverlay = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setPreviewOpen(false);
      setSendCountdown(null);
    };
    window.addEventListener("keydown", closeOverlay);
    return () => window.removeEventListener("keydown", closeOverlay);
  }, [previewOpen, sendCountdown]);

  useEffect(() => {
    if (sendCountdown === 5) cancelSendRef.current?.focus();
  }, [sendCountdown]);

  useEffect(() => {
    if (!justSent) return;
    const timer = window.setTimeout(() => setJustSent(false), 2_800);
    return () => window.clearTimeout(timer);
  }, [justSent]);

  const queueSave = useCallback((nextDocument: DraftDocument, nextTitle: string, revision: number) => {
    setSaveState("saving");
    const task = saveQueueRef.current.then(async () => {
      const currentDraftId = draftIdRef.current;
      if (!currentDraftId) throw new Error("Draft is unavailable");
      let expectedVersion = versionRef.current;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const saved = await api.saveDraft(currentDraftId, nextDocument, expectedVersion, nextTitle);
          versionRef.current = saved.version;
          savedRevisionRef.current = revision;
          savedDocumentRef.current = nextDocument;
          savedTitleRef.current = nextTitle;
          setDraft((current) =>
            current ? { ...current, version: saved.version, updatedAt: saved.updatedAt } : current,
          );
          setSaveState(revision === revisionRef.current ? "saved" : "unsaved");
          return;
        } catch (cause) {
          if (cause instanceof ApiError && cause.status === 403 && cause.message.includes("banned")) {
            setBanned(true);
            throw cause;
          }
          if (!(cause instanceof ApiError && cause.status === 409)) throw cause;
          // Another tab saved this draft. Reconcile instead of erroring: if we
          // have no local edits, adopt the server's newer version; otherwise
          // our newer edits win and we retry against the fresh version.
          const fresh = await api.draft(currentDraftId);
          versionRef.current = fresh.version;
          if (revisionRef.current === savedRevisionRef.current) {
            savedDocumentRef.current = fresh.document;
            savedTitleRef.current = fresh.title;
            savedRevisionRef.current = revision;
            setDocument(fresh.document);
            setTitle(fresh.title);
            setDraft((current) =>
              current ? { ...current, version: fresh.version, updatedAt: fresh.updatedAt } : current,
            );
            setSaveState("saved");
            return;
          }
          expectedVersion = fresh.version;
        }
      }
      setSaveState("error");
      throw new Error("Couldn’t save the draft");
    });
    saveQueueRef.current = task.catch(() => undefined);
    return task;
  }, []);

  const selectIdentity = useCallback(async (identity: { id: string; name: string; avatarUrl: string }) => {
    const currentDraftId = draftIdRef.current;
    if (!currentDraftId) return;
    setSaveState("saving");
    try {
      const saved = await api.saveDraft(
        currentDraftId,
        documentRef.current ?? { version: 1, sections: [] },
        versionRef.current,
        titleRef.current,
        identity.id,
      );
      versionRef.current = saved.version;
      setSaveState("saved");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 403 && cause.message.includes("banned")) {
        setBanned(true);
        return;
      }
      setSaveState("error");
    }
  }, []);

  const clearIdentity = useCallback(async () => {
    const currentDraftId = draftIdRef.current;
    if (!currentDraftId) return;
    setSaveState("saving");
    try {
      const saved = await api.saveDraft(
        currentDraftId,
        documentRef.current ?? { version: 1, sections: [] },
        versionRef.current,
        titleRef.current,
        null,
      );
      versionRef.current = saved.version;
      setSaveState("saved");
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 403 && cause.message.includes("banned")) {
        setBanned(true);
        return;
      }
      setSaveState("error");
    }
  }, []);

  useEffect(() => {
    if (saveState !== "unsaved" || !document || !draftIdRef.current) return;
    const revision = revisionRef.current;
    const timer = window.setTimeout(() => void queueSave(document, title, revision).catch(() => undefined), 350);
    return () => window.clearTimeout(timer);
  }, [document, queueSave, saveState, title]);

  // Retry a failed save automatically so a dropped connection never strands
  // the draft in an error state.
  useEffect(() => {
    if (saveState !== "error") return;
    const timer = window.setTimeout(() => setSaveState("unsaved"), 4_000);
    return () => window.clearTimeout(timer);
  }, [saveState]);

  const pushUndoSnapshot = () => {
    const currentDraftId = draftIdRef.current;
    const current = documentRef.current;
    if (!currentDraftId || !current) return;
    const now = Date.now();
    if (now - lastUndoAtRef.current < 800) return;
    const top = undoRef.current[undoRef.current.length - 1];
    if (top && JSON.stringify(top.document) === JSON.stringify(current)) return;
    lastUndoAtRef.current = now;
    undoRef.current = [...undoRef.current, { document: current, title: titleRef.current, at: now }].slice(-MAX_UNDO);
    redoRef.current = [];
    saveHistory(currentDraftId, undoRef.current, redoRef.current);
  };

  const undo = useCallback(() => {
    const currentDraftId = draftIdRef.current;
    const current = documentRef.current;
    if (!currentDraftId || !current) return;
    const previous = undoRef.current[undoRef.current.length - 1];
    if (!previous) return;
    undoRef.current = undoRef.current.slice(0, -1);
    redoRef.current = [...redoRef.current, { document: current, title: titleRef.current, at: Date.now() }].slice(
      -MAX_REDO,
    );
    saveHistory(currentDraftId, undoRef.current, redoRef.current);
    setDocument(previous.document);
    setTitle(previous.title);
    setSaveState("unsaved");
  }, []);

  const redo = useCallback(() => {
    const currentDraftId = draftIdRef.current;
    const current = documentRef.current;
    if (!currentDraftId || !current) return;
    const next = redoRef.current[redoRef.current.length - 1];
    if (!next) return;
    redoRef.current = redoRef.current.slice(0, -1);
    undoRef.current = [...undoRef.current, { document: current, title: titleRef.current, at: Date.now() }].slice(
      -MAX_UNDO,
    );
    saveHistory(currentDraftId, undoRef.current, redoRef.current);
    setDocument(next.document);
    setTitle(next.title);
    setSaveState("unsaved");
  }, []);

  useEffect(() => {
    if (!document) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const meta = event.metaKey || event.ctrlKey;
      if (!meta) return;
      const active = window.document.activeElement;
      if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return;
      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      } else if (key === "y") {
        event.preventDefault();
        redo();
      }
    };
    window.document.addEventListener("keydown", onKeyDown);
    return () => window.document.removeEventListener("keydown", onKeyDown);
  }, [document, redo, undo]);

  // Lightweight realtime sync: when this tab is idle and a newer version
  // appears on the server (another tab, another device), adopt it so edits
  // show up without a reload. Active local edits always win.
  useEffect(() => {
    const currentDraftId = draftIdRef.current;
    if (!currentDraftId || draft?.status !== "draft") return;
    let alive = true;
    const poll = async () => {
      if (!alive || window.document.hidden) return;
      try {
        const head = await api.draftHead(currentDraftId);
        if (!alive || head.version <= versionRef.current) return;
        if (saveStateRef.current !== "saved") return;
        const fresh = await api.draft(currentDraftId);
        if (!alive || saveStateRef.current !== "saved" || head.version !== fresh.version) return;
        versionRef.current = fresh.version;
        savedDocumentRef.current = fresh.document;
        savedTitleRef.current = fresh.title;
        savedRevisionRef.current = revisionRef.current;
        setDocument(fresh.document);
        setTitle(fresh.title);
        setDraft((current) => (current ? { ...current, version: fresh.version, updatedAt: fresh.updatedAt } : current));
      } catch {
        // Transient poll failures are harmless.
      }
    };
    const interval = window.setInterval(poll, 6_000);
    const onVisible = () => {
      if (!window.document.hidden) void poll();
    };
    window.document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      window.clearInterval(interval);
      window.document.removeEventListener("visibilitychange", onVisible);
    };
  }, [draft?.status]);

  const changeDocument = (updater: (current: DraftDocument) => DraftDocument) => {
    if (draft?.status === "sent" && !editingSent) return;
    pushUndoSnapshot();
    revisionRef.current += 1;
    setDocument((current) => (current ? updater(current) : current));
    setSaveState("unsaved");
  };
  const changeTitle = (nextTitle: string) => {
    if (draft?.status === "sent" && !editingSent) return;
    pushUndoSnapshot();
    revisionRef.current += 1;
    setTitle(nextTitle);
    setSaveState("unsaved");
  };
  const updateSection = (updated: MessageSection) =>
    changeDocument((current) => ({
      ...current,
      sections: current.sections.map((section) => (section.id === updated.id ? updated : section)),
    }));
  const removeSection = (id: string) => {
    if (openSectionId === id) setOpenSectionId(null);
    changeDocument((current) => ({ ...current, sections: current.sections.filter((section) => section.id !== id) }));
  };
  const moveSection = (index: number, direction: -1 | 1) =>
    changeDocument((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.sections.length) return current;
      const currentSection = current.sections[index];
      const targetSection = current.sections[target];
      if (!currentSection || !targetSection) return current;
      return { ...current, sections: reorderSections(current.sections, currentSection.id, targetSection.id) };
    });
  const finishSectionDrag = (event: DragEndEvent) => {
    setDraggedSectionId(null);
    if (!event.over || event.active.id === event.over.id) return;
    changeDocument((current) => {
      const sections = reorderSections(current.sections, String(event.active.id), String(event.over?.id));
      return sections === current.sections ? current : { ...current, sections };
    });
  };
  const addTextSection = () => {
    const id = newId();
    setOpenSectionId(id);
    changeDocument((current) => ({
      ...current,
      sections: appendBeforeTrailingPing(current.sections, { id, type: "text", mrkdwn: "" }),
    }));
  };
  const addActionsSection = () => {
    const id = newId();
    setOpenSectionId(id);
    changeDocument((current) => ({
      ...current,
      sections: appendBeforeTrailingPing(current.sections, {
        id,
        type: "actions",
        actions: [
          {
            id: `action_${newId().slice(0, 8)}`,
            label: "Learn more",
            behavior: "open_url",
            url: "https://example.com",
            style: "default",
          },
        ],
      }),
    }));
  };
  const addVoteSection = () => {
    const id = newId();
    setOpenSectionId(id);
    changeDocument((current) => ({
      ...current,
      sections: appendBeforeTrailingPing(current.sections, {
        id,
        type: "vote",
        question: "*What came first: the egg or the chicken?*",
        options: [
          { id: `vote_${newId().slice(0, 8)}`, label: "Egg" },
          { id: `vote_${newId().slice(0, 8)}`, label: "Chicken" },
        ],
        selectionMode: "single",
        maxSelections: 1,
        allowChange: true,
        showVoters: true,
        showCounts: true,
        showPercentages: true,
        showBars: true,
        showZeroVoteOptions: true,
        confirmationText: ":yay: You picked *{{vote_option}}*.",
      }),
    }));
  };
  const addDividerSection = () => {
    const id = newId();
    setOpenSectionId(id);
    changeDocument((current) => ({
      ...current,
      sections: appendBeforeTrailingPing(current.sections, { id, type: "divider" }),
    }));
  };
  const addHeadingSection = () => {
    if (hasHeading) return;
    const id = newId();
    setOpenSectionId(id);
    changeDocument((current) => {
      const primaryIndex = current.sections.findIndex((section) => section.type === "text");
      const sections = [...current.sections];
      sections.splice(Math.max(0, primaryIndex), 0, {
        id,
        type: "heading",
        text: "Announcement",
        level: 1,
      });
      return { ...current, sections };
    });
  };
  const addContextSection = () => {
    const id = newId();
    setOpenSectionId(id);
    changeDocument((current) => ({
      ...current,
      sections: appendBeforeTrailingPing(current.sections, { id, type: "context", text: "Add a helpful detail" }),
    }));
  };
  const addTableSection = () => {
    const id = newId();
    setOpenSectionId(id);
    changeDocument((current) => ({
      ...current,
      sections: appendBeforeTrailingPing(current.sections, {
        id,
        type: "table",
        rows: [
          {
            id: newId(),
            cells: [
              { id: newId(), text: "Column 1" },
              { id: newId(), text: "Column 2" },
            ],
          },
          {
            id: newId(),
            cells: [
              { id: newId(), text: "" },
              { id: newId(), text: "" },
            ],
          },
        ],
      }),
    }));
  };
  const addMarkdownSection = () => {
    const id = newId();
    setOpenSectionId(id);
    changeDocument((current) => ({
      ...current,
      sections: appendBeforeTrailingPing(current.sections, { id, type: "markdown", text: "" }),
    }));
  };

  const uploadFiles = async (files: File[]) => {
    if (!draft || !document) return;
    setError("");
    setErrorDetail("");
    setBlockErrors([]);
    for (const file of files) {
      try {
        setUploadProgress(0);
        const result = await api.upload(draft.id, file, setUploadProgress);
        const type = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : "file";
        const sectionId = newId();
        setOpenSectionId(sectionId);
        changeDocument((current) => ({
          ...current,
          sections: appendBeforeTrailingPing(current.sections, {
            id: sectionId,
            type,
            assetId: result.assetId,
            name: file.name,
            url: result.publicUrl,
            mimeType: file.type,
            size: file.size,
            alt: "",
          }),
        }));
      } catch (cause) {
        if (cause instanceof ApiError && cause.status === 403 && cause.message.includes("banned")) setBanned(true);
        setError(cause instanceof Error ? cause.message : "Upload failed");
      } finally {
        setUploadProgress(null);
      }
    }
  };

  const send = async () => {
    if (
      !draft ||
      !document ||
      sending ||
      uploadProgress !== null ||
      (draft.status === "sent" && !editingSent) ||
      saveState === "conflict"
    )
      return;
    setSending(true);
    setError("");
    setErrorDetail("");
    setBlockErrors([]);
    try {
      const revision = revisionRef.current;
      if (savedRevisionRef.current !== revision || saveState === "saving" || saveState === "error") {
        await queueSave(document, title, revision);
      }
      const sent = await api.sendDraft(draft.id);
      clearHistory(draft.id);
      setJustSent(true);
      setEditingSent(false);
      setDraft((current) =>
        current
          ? {
              ...current,
              status: "sent",
              publishedMessageId: sent.messageId,
              sender: { ...current.sender, name: sent.senderName, selectedName: sent.senderName },
            }
          : current,
      );
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 403 && cause.message.includes("banned")) setBanned(true);
      const pingBody =
        cause instanceof ApiError ? (cause.body as { pingPermission?: { context?: unknown } } | undefined) : undefined;
      if (pingBody?.pingPermission?.context) {
        const context = pingBody.pingPermission.context as Awaited<ReturnType<typeof api.pingPermission>>;
        setPingPermission(context);
        setPingNeedsRequest(!context.allowed);
        setPingModalOpen(true);
      } else {
        setPingPermission(null);
        setPingModalOpen(false);
      }
      setError(cause instanceof Error ? cause.message : "Send failed");
      const body = cause instanceof ApiError ? ((cause.body ?? {}) as Record<string, unknown>) : {};
      setErrorDetail(typeof body.detail === "string" ? body.detail : "");
      const blocks = Array.isArray(body.blockErrors)
        ? (body.blockErrors as Array<{ sectionId: string; message: string }>)
        : [];
      setBlockErrors(blocks);
      if (blocks.length) {
        const first = blocks[0];
        if (first?.sectionId) {
          setOpenSectionId((current) => current ?? first.sectionId);
          requestAnimationFrame(() => {
            globalThis.document
              .querySelector(`[data-section-id="${CSS.escape(first.sectionId)}"]`)
              ?.scrollIntoView({ behavior: "smooth", block: "center" });
          });
        }
      }
    } finally {
      setSending(false);
    }
  };

  const sendRef = useRef(send);
  useEffect(() => {
    sendRef.current = send;
  });
  useEffect(() => {
    if (sendCountdown === null) return;
    const timer = window.setTimeout(() => {
      if (sendCountdown === 1) {
        setSendCountdown(null);
        void sendRef.current();
        return;
      }
      setSendCountdown(sendCountdown - 1);
    }, 1_000);
    return () => window.clearTimeout(timer);
  }, [sendCountdown]);

  const beginSend = () => {
    if (
      !draft ||
      !document ||
      sending ||
      uploadProgress !== null ||
      (draft.status === "sent" && !editingSent) ||
      saveState === "conflict"
    )
      return;
    if (draft.status === "sent" && editingSent) {
      void send();
      return;
    }
    setPreviewOpen(false);
    // Re-check ping permission right before sending: if they've since become a
    // manager/creator (or never needed it), send as normal with the countdown;
    // if they still can't ping, open the ask modal directly instead.
    if (draft.ping === "none") {
      setSendCountdown(5);
      return;
    }
    setCheckingPing(true);
    api
      .pingPermission(draft.id)
      .then((context) => {
        setPingPermission(context);
        setPingNeedsRequest(!context.allowed);
        if (context.allowed) {
          setPingModalOpen(false);
          setSendCountdown(5);
        } else {
          setSendCountdown(null);
          setPingModalOpen(true);
        }
      })
      .catch(() => {
        setSendCountdown(5);
      })
      .finally(() => {
        setCheckingPing(false);
      });
  };
  if (banned) return <BannedState />;
  if (!draft || !document) {
    if (error) return <AccessRecovery signedIn={recoverySignedIn} />;
    return (
      <main className="loading-screen">
        <img className="loading-mark" src={bchannelLogo} alt="bChannel" />
        <LoadingRaccoon size={42} />
        <span>Opening your draft…</span>
      </main>
    );
  }

  const stats = {
    richText: document.sections
      .filter((section) => section.type === "text")
      .reduce((total, section) => total + section.mrkdwn.length, 0),
    markdown: document.sections
      .filter((section) => section.type === "markdown")
      .reduce((total, section) => total + section.text.length, 0),
    blocks: document.sections.reduce((total, section) => {
      if (section.type === "vote") {
        const resultsVisible = section.showVoters || section.showCounts || section.showPercentages || section.showBars;
        return total + 1 + (resultsVisible ? 1 : 0) + Math.ceil(section.options.length / 5);
      }
      if (section.type === "actions")
        return total + 1 + section.actions.filter((action) => action.behavior === "rsvp" && action.rsvp).length;
      return total + 1;
    }, 0),
    media: document.sections.filter((section) => ["image", "video", "file"].includes(section.type)).length,
  };
  const primaryTextId = document.sections.find((section) => section.type === "text")?.id;
  const primaryTextIndex = document.sections.findIndex((section) => section.id === primaryTextId);
  const textSectionCount = document.sections.filter((section) => section.type === "text").length;
  const hasHeading = document.sections.some((section) => section.type === "heading");

  return (
    <div className="app-shell">
      <div className="checkmark-preloader" aria-hidden="true">
        <DotLottieReact src={checkmarkAnimation} autoplay={false} />
      </div>
      <main
        className={`composer-layout ${dropActive ? "drop-active" : ""}`}
        aria-label="Message composer"
        onDragOver={(event) => {
          event.preventDefault();
          setDropActive(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) setDropActive(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDropActive(false);
          uploadFiles(Array.from(event.dataTransfer.files));
        }}
      >
        {dropActive && (
          <div className="drop-overlay">
            <Image size={30} />
            <strong>Drop files anywhere</strong>
            <span>They’ll be added to this message</span>
          </div>
        )}

        <section className="compose-pane">
          <header className="composer-context">
            <div className="composer-context-main">
              <a className="composer-home" href="/" aria-label="Back to drafts" title="Back to drafts">
                <img src={bchannelLogo} alt="" />
              </a>
              <div className="composer-draft-heading">
                <label className="visually-hidden" htmlFor="composer-draft-title">
                  Draft title
                </label>
                <input
                  id="composer-draft-title"
                  className="composer-draft-title"
                  value={title}
                  placeholder="Untitled message"
                  maxLength={120}
                  disabled={draft.status === "sent" && !editingSent}
                  onChange={(event) => changeTitle(event.target.value)}
                />
                <div className="composer-targets">
                  <span className="context-channel">
                    <Hash size={13} /> {draft.channel.name}
                  </span>
                  <span
                    className="context-ping"
                    title={
                      draft.ping === "none"
                        ? "This message has no channel-wide ping"
                        : `This message notifies @${draft.ping}`
                    }
                  >
                    {draft.ping === "none" ? <MessageSquareText size={13} /> : <AtSign size={13} />}
                    {draft.ping === "none" ? "No channel ping" : `@${draft.ping}`}
                  </span>
                </div>
              </div>
            </div>
            <div className="context-account">
              <SaveIndicator state={saveState} />
              <SenderNamePicker
                sender={draft.sender}
                identities={identities}
                canManageIdentities={Boolean(draft.canManageIdentities)}
                disabled={draft.status === "sent"}
                onChange={(sender) => setDraft((current) => (current ? { ...current, sender } : current))}
                onSelectIdentity={(identity) => void selectIdentity(identity)}
                onClearIdentity={() => void clearIdentity()}
                onChanged={() =>
                  api
                    .identities()
                    .then(setIdentities)
                    .catch(() => undefined)
                }
              />
            </div>
          </header>

          {draft.status === "sent" && !editingSent ? (
            <div className={`sent-state ${justSent ? "just-sent" : ""}`}>
              <div className="sent-confirmation">
                <span className="sent-check" aria-hidden="true">
                  {justSent ? (
                    <DotLottieReact
                      className="sent-check-lottie"
                      src={checkmarkAnimation}
                      autoplay={!window.matchMedia("(prefers-reduced-motion: reduce)").matches}
                    />
                  ) : (
                    <Check className="sent-check-static" size={22} />
                  )}
                </span>
                <span className="sent-copy">
                  <strong>Sent to #{draft.channel.name}</strong>
                  <small>Posted as {draft.sender.selectedName}</small>
                </span>
              </div>
              <div className="sent-actions">
                <button type="button" onClick={() => setPreviewOpen(true)}>
                  View
                </button>
                <button type="button" onClick={() => setEditingSent(true)}>
                  Edit
                </button>
              </div>
            </div>
          ) : (
            <>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={(event) => setDraggedSectionId(String(event.active.id))}
                onDragCancel={() => setDraggedSectionId(null)}
                onDragEnd={finishSectionDrag}
              >
                <SortableContext
                  items={document.sections.map((section) => section.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className={`section-stack${draggedSectionId ? " sorting" : ""}`}>
                    {document.sections.map((section, index) => {
                      return (
                        <SortableSection key={section.id} id={section.id} label={sectionLabel(section)}>
                          {({ setNodeRef, dragHandle, stateClass }) => (
                            <article
                              ref={setNodeRef}
                              className={`message-section ${section.type} ${section.id === primaryTextId ? "primary-text" : "accordion"} ${
                                section.type === "heading" && index < primaryTextIndex ? "above-primary" : ""
                              } ${openSectionId === section.id ? "open" : "collapsed"} ${
                                blockErrors.some((block) => block.sectionId === section.id) ? "block-error" : ""
                              }${stateClass}`}
                              data-section-id={section.id}
                            >
                              {section.id === primaryTextId ? (
                                <div className="primary-section-header">
                                  <span className="primary-section-title">
                                    <strong>Message</strong>
                                    <small>Slack formatting supported</small>
                                  </span>
                                  <SectionControls
                                    index={index}
                                    count={document.sections.length}
                                    canDelete={false}
                                    canMoveUp={index > 0}
                                    canMoveDown={index < document.sections.length - 1}
                                    dragHandle={dragHandle}
                                    onMove={(direction) => moveSection(index, direction)}
                                    onDelete={() => undefined}
                                  />
                                </div>
                              ) : (
                                <>
                                  <SectionAccordionHeader
                                    section={section}
                                    open={openSectionId === section.id}
                                    onToggle={() =>
                                      setOpenSectionId((current) => (current === section.id ? null : section.id))
                                    }
                                  />
                                  <SectionControls
                                    index={index}
                                    count={document.sections.length}
                                    canDelete={
                                      document.sections.length > 1 &&
                                      !(section.type === "text" && textSectionCount === 1)
                                    }
                                    canMoveUp={index > 0}
                                    canMoveDown={index < document.sections.length - 1}
                                    dragHandle={dragHandle}
                                    onMove={(direction) => moveSection(index, direction)}
                                    onDelete={() => removeSection(section.id)}
                                  />
                                </>
                              )}
                              {(section.id === primaryTextId || openSectionId === section.id) && (
                                <div
                                  className={
                                    section.id === primaryTextId
                                      ? "primary-section-content"
                                      : "section-accordion-content"
                                  }
                                  id={section.id === primaryTextId ? undefined : `section-content-${section.id}`}
                                >
                                  {section.type === "text" && (
                                    <TextEditor
                                      section={section}
                                      ping={draft.ping}
                                      primary={section.id === primaryTextId}
                                      placeholder={
                                        section.id === primaryTextId ? `Message #${draft.channel.name}` : undefined
                                      }
                                      onChange={updateSection}
                                      onAttach={() => fileInput.current?.click()}
                                      onAddText={addTextSection}
                                      onAddActions={addActionsSection}
                                      onAddVote={addVoteSection}
                                      onAddDivider={addDividerSection}
                                      onAddHeading={addHeadingSection}
                                      onAddContext={addContextSection}
                                      onAddTable={addTableSection}
                                      onAddMarkdown={addMarkdownSection}
                                      onSend={beginSend}
                                      onPreview={() => setPreviewOpen(true)}
                                      sending={sending}
                                      uploading={uploadProgress !== null}
                                      sent={draft.status === "sent" && !editingSent}
                                      editingMessage={draft.status === "sent" && editingSent}
                                      saveState={saveState}
                                      unfurlLinks={document.unfurlLinks !== false}
                                      onToggleUnfurlLinks={() =>
                                        changeDocument((current) => ({
                                          ...current,
                                          unfurlLinks: current.unfurlLinks === false,
                                        }))
                                      }
                                      stats={stats}
                                      hasHeading={hasHeading}
                                      needsPingPermission={draft.ping !== "none" && pingNeedsRequest}
                                      checkingPing={checkingPing}
                                    />
                                  )}
                                  {(section.type === "image" ||
                                    section.type === "video" ||
                                    section.type === "file") && (
                                    <MediaEditor section={section} onChange={updateSection} />
                                  )}
                                  {section.type === "actions" && (
                                    <ActionsEditor
                                      section={section}
                                      onChange={updateSection}
                                      authorTimezone={
                                        Intl.DateTimeFormat().resolvedOptions().timeZone ||
                                        draft.sender.timezone ||
                                        "UTC"
                                      }
                                    />
                                  )}
                                  {section.type === "vote" && <VoteEditor section={section} onChange={updateSection} />}
                                  {section.type === "heading" && (
                                    <HeadingEditor section={section} onChange={updateSection} />
                                  )}
                                  {section.type === "context" && (
                                    <ContextEditor section={section} onChange={updateSection} />
                                  )}
                                  {section.type === "table" && (
                                    <TableEditor section={section} onChange={updateSection} />
                                  )}
                                  {section.type === "markdown" && (
                                    <MarkdownEditor section={section} onChange={updateSection} />
                                  )}
                                  {section.type === "divider" && (
                                    <div className="divider-editor">
                                      <span>Divider</span>
                                      <hr />
                                    </div>
                                  )}
                                  {blockErrors
                                    .filter((block) => block.sectionId === section.id)
                                    .map((block) => (
                                      <div className="section-block-error" key={block.sectionId} role="alert">
                                        <span>{block.message}</span>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </article>
                          )}
                        </SortableSection>
                      );
                    })}
                  </div>
                </SortableContext>
                <DragOverlay dropAnimation={{ duration: 160, easing: "ease-out" }}>
                  {draggedSectionId ? (
                    <div className="section-drag-preview">
                      <GripVertical size={16} />
                      <strong>
                        {sectionLabel(document.sections.find((section) => section.id === draggedSectionId))}
                      </strong>
                    </div>
                  ) : null}
                </DragOverlay>
              </DndContext>

              <input
                ref={fileInput}
                hidden
                type="file"
                multiple
                accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,application/pdf,text/plain,text/csv,application/zip"
                onChange={(event) => {
                  const files = Array.from(event.target.files ?? []);
                  event.currentTarget.value = "";
                  void uploadFiles(files);
                }}
              />
              {uploadProgress !== null && (
                <div className="upload-progress">
                  <span style={{ width: `${uploadProgress}%` }} />
                  <div>{uploadProgress === 100 ? "Finishing…" : `Uploading… ${uploadProgress}%`}</div>
                </div>
              )}
              {pingModalOpen && pingPermission && (
                <PingPermissionCard
                  draftId={draft.id}
                  info={pingPermission}
                  channelName={draft.channel.name}
                  ping={draft.ping === "none" ? undefined : draft.ping}
                  onDismiss={() => {
                    setPingModalOpen(false);
                  }}
                />
              )}
              {error && !pingModalOpen && (
                <div className={`error-banner${error === POSTING_PERMISSION_ERROR ? " error-banner--with-help" : ""}`}>
                  <div className="error-banner__content">
                    <span>{error}</span>
                    {errorDetail && <code className="error-banner__detail">{errorDetail}</code>}
                    {error === POSTING_PERMISSION_ERROR && (
                      <a href={POSTING_PERMISSION_HELP_IMAGE} target="_blank" rel="noreferrer">
                        <img
                          src={POSTING_PERMISSION_HELP_IMAGE}
                          alt="Slack Channel permissions showing bChannel added under Admins, plus specific people"
                        />
                        <span>
                          Open the full-size instructions <ExternalLink size={11} />
                        </span>
                      </a>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setError("");
                      setErrorDetail("");
                      setBlockErrors([]);
                    }}
                  >
                    <X size={15} />
                  </button>
                </div>
              )}
            </>
          )}
          {draft.status === "sent" && !editingSent && draft.publishedMessageId && (
            <ResultsPanel messageId={draft.publishedMessageId} />
          )}
        </section>

        {previewOpen && (
          <div className="preview-layer">
            <button
              type="button"
              className="preview-backdrop"
              aria-label="Close message preview"
              onClick={() => setPreviewOpen(false)}
            />
            <div className="preview-sheet" role="dialog" aria-modal="true" aria-label="Message preview">
              <MessagePreview draft={draft} document={document} onClose={() => setPreviewOpen(false)} />
            </div>
          </div>
        )}
        {sendCountdown !== null && (
          <div className="send-preflight-layer">
            <button
              type="button"
              className="send-preflight-backdrop"
              aria-label="Cancel sending"
              onClick={() => setSendCountdown(null)}
            />
            <section
              className="send-preflight"
              role="dialog"
              aria-modal="true"
              aria-labelledby="send-preflight-title"
              aria-describedby="send-preflight-description"
            >
              <header className="send-preflight-head">
                <span
                  className="send-countdown"
                  style={{
                    background: `conic-gradient(var(--green) ${(sendCountdown / 5) * 360}deg, #3b3b3e 0deg)`,
                  }}
                  aria-hidden="true"
                >
                  <span>{sendCountdown}</span>
                </span>
                <div>
                  <strong id="send-preflight-title" title={`#${draft.channel.name}`}>
                    Sending to <span>#{draft.channel.name}</span>
                  </strong>
                  <span id="send-preflight-description" aria-live="polite">
                    Posting as {draft.sender.selectedName} · Sending in {sendCountdown}{" "}
                    {sendCountdown === 1 ? "second" : "seconds"} · Is everything correct?
                  </span>
                </div>
                <button ref={cancelSendRef} type="button" onClick={() => setSendCountdown(null)}>
                  Cancel
                </button>
              </header>
              <div className="send-preflight-preview">
                <MessagePreview draft={draft} document={document} embedded />
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

export function ComposerPage() {
  return <ComposerApp />;
}
